import PropTypes from 'prop-types';
import React from 'react';
import $ from 'jquery';
import _ from 'underscore';
import classnames from 'classnames';
import './index.scss';

const OptionKeyPropType = PropTypes.oneOfType([
  PropTypes.number,
  PropTypes.string,
]);

class Dropdown extends React.PureComponent {
  static displayName = 'Dropdown';

  static propTypes = {
    options: PropTypes.objectOf(OptionKeyPropType).isRequired,
    subLabels: PropTypes.objectOf(PropTypes.string),
    showSubLabels: PropTypes.bool,
    value: OptionKeyPropType,
    disabledOptionKeys: PropTypes.objectOf(PropTypes.bool),
    defaultOption: OptionKeyPropType,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
    displayOptions: PropTypes.shape({
      minimal: PropTypes.bool,
      compact: PropTypes.bool,
      inline: PropTypes.bool,
    }),
    icon: PropTypes.node,
    disabled: PropTypes.bool,
    hasTicketCapacityEnabled: PropTypes.bool,
  };

  static defaultProps = {
    selectedOption: null,
    disabledOptionKeys: {},
    subLabels: {},
    showSubLabels: true,
    defaultOption: null,
    placeholder: '',
    displayOptions: {
      search: false,
      minimal: false,
      compact: true,
      inline: false,
    },
    icon: <i className="af-angle-down-ar icon icon-anyfont" />,
    disabled: false,
    hasTicketCapacityEnabled: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
    };
  }

  componentDidMount() {
    if (!this.props.disabled) {
      this._initSemantic();
    }
  }

  _initSemantic = () => {
    const _this = this;
    if (this._isSemantic()) {
      this._$semanticDropdown().dropdown({
        onChange(value, label, $el) {
          // There's an initial trigger with an undef value
          // when it renders. Don't have time to findout why
          if (value === undefined) {
            return;
          }
          if (typeof _this.props.onChange === 'function') {
            _this.props.onChange(_this.props.name, value, label, $el);
          }
        },
        onHide() {
          _this.setState({ expanded: false });
        },
        onShow() {
          _this.setState({ expanded: true });
        },
      });
    }
  };

  _destroySemantic = () => {
    this._$semanticDropdown().dropdown('destroy');
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      this._isSemantic() &&
      prevProps.disabled === true &&
      this.props.disabled === false
    ) {
      this._initSemantic();
    }

    if (
      this._isSemantic() &&
      prevProps.disabled === false &&
      this.props.disabled === true
    ) {
      this._destroySemantic();
    }

    if (this._isSemantic() && this.props.value !== prevProps.value) {
      this._$semanticDropdown().dropdown('set selected', this.props.value);
    }
  }

  showDropdown = () => {
    if (this._isSemantic()) {
      this._$semanticDropdown().dropdown('show');
    }
  };

  _isSemantic = () => {
    return window.mobileDetect && window.mobileDetect.mobile() === null;
  };

  _$semanticDropdown = () => {
    return $(this.refs.dropdown);
  };

  _nativeOptionDidChange = (e) => {
    this.props.onChange(this.props.name, e.target.value, e);
  };

  _renderDropdownOption = (pair) => {
    let className = 'item';
    const label = pair[1];
    let subLabel = null;
    const value = pair[0];

    if (this.props.showSubLabels && this.props.subLabels[value] !== null) {
      subLabel = (
        <span className="subLabel" data-testid="sub-label">
          {this.props.subLabels[value]}
        </span>
      );
    }

    if (this._isOptionDisabled(value)) {
      className += ' disabled';
    }

    return (
      <div
        key={value}
        className={className}
        data-value={value}
        data-text={label}
        data-testid="guest-dropdown-option"
      >
        {label}
        {subLabel}
      </div>
    );
  };

  _renderDropdown = () => {
    if (!this._isSemantic()) {
      return this._renderNativeDropdown();
    }
    return this._renderWebDropdown();
  };

  _renderWebDropdown = () => {
    return (
      <div
        className="menu"
        data-testid="web-dropdown"
        dataName={this.props.name}
      >
        {_.map(_.pairs(this.props.options), this._renderDropdownOption)}
      </div>
    );
  };

  _renderNativeOption = (pair) => {
    const value = pair[0];
    const label = pair[1];
    const subLabel = this._sublabelForNativeOption(value);
    const disabled = this._isOptionDisabled(value);

    return (
      <option value={value} key={value} disabled={disabled}>
        {label}
        {subLabel}
      </option>
    );
  };

  _sublabelForNativeOption = (optionKey) => {
    let subLabel = null;

    if (!!this.props.subLabels[optionKey] === true) {
      subLabel = ` (${this.props.subLabels[optionKey]})`;
    }

    return subLabel;
  };

  _isOptionDisabled = (optionKey) => {
    const { disabledOptionKeys, hasTicketCapacityEnabled } = this.props;

    return disabledOptionKeys[optionKey] === !hasTicketCapacityEnabled;
  };

  _renderNativeDropdown = () => {
    return (
      <div className="native-dropdown">
        <select
          ref="nativeSelect"
          name={this.props.name}
          value={this.props.value}
          onChange={this._nativeOptionDidChange}
          defaultValue={null}
        >
          <option value="" key="initial" selected disabled>
            {this.props.placeholder
              ? this.props.placeholder
              : 'Choose an option'}
          </option>
          {_.map(_.pairs(this.props.options), this._renderNativeOption)}
        </select>
      </div>
    );
  };

  _renderError = () => {
    if (this._hasError()) {
      return <span className="dropdown-error">{this.props.errors[0]}</span>;
    }
    return null;
  };

  _labelFor = (value) => {
    return this.props.options[value];
  };

  _theme = () => {
    return this.props.theme ? `t-${this.props.theme}` : '';
  };

  _hasError = () => {
    return this.props.errors && this.props.errors.length > 0;
  };

  render() {
    const value = this.props.value || this.props.defaultOption;
    const dropdownClasses = classnames('ui', 'dropdown', 'border-solid', {
      selection: !this.props.displayOptions.minimal,
      search: !!this.props.displayOptions.search,
      compact: this.props.displayOptions.compact,
      inline: this.props.displayOptions.inline,
      error: this._hasError(),
      disabled: this.props.disabled,
    });

    const dropdownWrapperClasses = classnames(
      'dropdown-wrapper',
      {
        error: this._hasError(),
      },
      this._theme()
    );
    return (
      <div ref="dropdownContainer" className={dropdownWrapperClasses}>
        <div
          ref="dropdown"
          className={dropdownClasses}
          data-testid={this.props['data-testid']}
          aria-expanded={this.state.expanded}
        >
          {value ? (
            <div className="text" data-testid="dropdown-text">
              {this._labelFor(value)}
            </div>
          ) : (
            <div data-testid="dropdown-select" className="default default-text">
              {this.props.placeholder}
            </div>
          )}

          {this.props.icon}

          {this._renderDropdown()}
        </div>
        {this.props.subtext && (
          <div
            className="question-subtext contains-brand-link"
            dangerouslySetInnerHTML={{ __html: this.props.subtext }}
          />
        )}
        {this._renderError()}
      </div>
    );
  }
}

export default Dropdown;
